/**
 * Shared INR / number formatting helpers.
 *
 * Extracted from IFADashboard, ClientDetail and InvestorHome, which each
 * carried near-identical private copies. The one real behavioural difference
 * between them is preserved by `fmtINR`'s `lakh` flag: the IFA book views
 * compact values ≥ ₹1L to "X.XX L" (book-wide totals get large), while the
 * individual investor view shows full rupees (smaller, single-portfolio
 * numbers read better un-compacted).
 */

/** Compact a rupee amount to crores (≥ ₹1Cr) or lakhs. */
/** Splits an amount into value and unit for callers that style them
 *  differently. THE CALLER MUST KEEP THEM ON ONE LINE — wrap the pair in an
 *  element with `white-space: nowrap` (see `.num` in pro.css). Unlike fmtINR
 *  this cannot protect itself, because it never joins the two halves. */
export function fmtCr(rupees: number): { value: string; unit: "Cr" | "L" } {
  if (rupees >= 1_00_00_000) {
    return { value: (rupees / 1_00_00_000).toFixed(2), unit: "Cr" };
  }
  return { value: (rupees / 1_00_000).toFixed(2), unit: "L" };
}

/**
 * Format a rupee amount with a leading sign.
 *  - `abs`  → drop the sign and show the magnitude only.
 *  - `lakh` → compact values ≥ ₹1L to "₹X.XX L" (default true). Pass false
 *             to always render full rupees via toLocaleString.
 */
export function fmtINR(
  n: number,
  { abs = false, lakh = true }: { abs?: boolean; lakh?: boolean } = {},
): string {
  const sign = !abs && n > 0 ? "+" : n < 0 ? "−" : "";
  const v = Math.abs(n);
  if (lakh && v >= 1_00_000) {
    // F19: NON-BREAKING space (U+00A0), not a plain one. A plain space here is
    // a wrap opportunity, and at 360-414px the unit routinely broke onto its
    // own line — the reader saw "₹2.55" with a lone "L" beneath it, which is
    // either meaningless or, worse, read as the whole amount. Fixing it in the
    // FORMATTER rather than with CSS means the guarantee travels with the
    // value into every caller, including ones that do not exist yet.
    return sign + "₹" + (v / 1_00_000).toFixed(2) + " L";
  }
  return sign + "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

/** First letters of up to the first two words, uppercased. "?" if empty. */
export function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}
